import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Users,
  Globe,
  BookOpen,
  Award,
  CheckCircle,
  ExternalLink,
  Mail,
  Clock,
  Star,
  FileText,
  Video,
  Presentation,
  Loader2
} from 'lucide-react';
import SNIICongressInfoSection from '@/components/congresos/SNIICongressInfoSection.jsx';

const Congresos = () => {
  const [congresosData, setCongresosData] = useState([]);
  const [fechaCongresos, setFechaCongresos] = useState("27 y 28 de noviembre de 2025");
  const [isLoading, setIsLoading] = useState(true);
  const [planes, setPlanes] = useState([]);

  // Configuración visual de los congresos (íconos y colores)
  const congresosConfig = {
    'ctes': { icono: Globe, color: 'from-blue-500 to-blue-600' },
    'cemys': { icono: BookOpen, color: 'from-green-500 to-green-600' },
    'cdes': { icono: Users, color: 'from-purple-500 to-purple-600' },
    'cifd': { icono: Award, color: 'from-orange-500 to-orange-600' },
    'cdhis': { icono: Star, color: 'from-red-500 to-red-600' },
    'cagi': { icono: FileText, color: 'from-indigo-500 to-indigo-600' }
  };

  // Cargar datos de todos los congresos desde un solo archivo
  useEffect(() => {
    const loadCongresosData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/data/congresos-lista.json?v=${Date.now()}`);
        if (!response.ok) {
          throw new Error('Error al cargar la lista de congresos');
        }
        const data = await response.json();

        // Combinar datos del JSON con configuración visual
        const congresosConIconos = Object.values(data.congresos).map(congreso => ({
          ...congreso,
          nombre: congreso.nombreCompleto,
          fecha: data.fechaGeneral,
          icono: congresosConfig[congreso.id]?.icono || Globe,
          color: congresosConfig[congreso.id]?.color || 'from-gray-500 to-gray-600',
          link: `/congresos/${congreso.id}`
        }));

        setCongresosData(congresosConIconos);
        setFechaCongresos(data.fechaGeneral);

        // Extraer planes dinámicamente desde datosGlobales.costos
        if (data.datosGlobales && data.datosGlobales.costos) {
          const costos = data.datosGlobales.costos;
          
          // Configuración visual por tipo de plan (se aplica según la clave)
          const estilosPlanes = {
            platino: {
              icono: Award,
              color: 'from-gray-500 to-gray-600',
              bgGradient: 'from-gray-100 to-gray-200',
              borderColor: 'border-gray-300',
              hoverBorder: 'hover:border-gray-600',
              checkColor: 'text-gray-700',
              badgeBg: 'bg-gray-600',
              destacado: false
            },
            oro: {
              icono: Star,
              color: 'from-yellow-500 to-yellow-600',
              bgGradient: 'from-yellow-100 to-orange-100',
              borderColor: 'border-yellow-400',
              hoverBorder: 'hover:border-[#e87729]',
              checkColor: 'text-[#e87729]',
              badgeBg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
              destacado: true
            },
            plata: {
              icono: Award,
              color: 'from-slate-400 to-slate-500',
              bgGradient: 'from-slate-50 to-slate-100',
              borderColor: 'border-slate-300',
              hoverBorder: 'hover:border-slate-500',
              checkColor: 'text-slate-600',
              badgeBg: 'bg-slate-500',
              destacado: false
            },
            bronce: {
              icono: Award,
              color: 'from-amber-600 to-amber-700',
              bgGradient: 'from-amber-50 to-amber-100',
              borderColor: 'border-amber-400',
              hoverBorder: 'hover:border-amber-600',
              checkColor: 'text-amber-700',
              badgeBg: 'bg-amber-600',
              destacado: false
            },
            // Estilo por defecto para nuevos planes
            default: {
              icono: Award,
              color: 'from-blue-500 to-blue-600',
              bgGradient: 'from-blue-50 to-blue-100',
              borderColor: 'border-blue-300',
              hoverBorder: 'hover:border-blue-600',
              checkColor: 'text-blue-700',
              badgeBg: 'bg-blue-600',
              destacado: false
            }
          };
          
          // Generar planes dinámicamente desde todas las claves de costos
          const planesArr = Object.keys(costos).map(clavePlan => {
            const datosPlan = costos[clavePlan];
            const estilos = estilosPlanes[clavePlan.toLowerCase()] || estilosPlanes.default;
            
            return {
              nombre: clavePlan.charAt(0).toUpperCase() + clavePlan.slice(1), // Capitalizar nombre
              ...estilos,
              descripcion: datosPlan.descripcion,
              beneficios: Array.isArray(datosPlan.revistas) ? datosPlan.revistas : (Array.isArray(datosPlan.beneficios) ? datosPlan.beneficios : []),
              precio: datosPlan.precio,
              moneda: datosPlan.moneda,
              tipo: datosPlan.tipo,
              nota: datosPlan.nota,
              destacado: datosPlan.destacado !== undefined ? datosPlan.destacado : estilos.destacado
            };
          });
          
          setPlanes(planesArr);
          console.log('Planes cargados dinámicamente:', planesArr);
        }
      } catch (err) {
        console.error('Error cargando congresos:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadCongresosData();
  }, []);

  const congresos = congresosData;

   // Mostrar estado de carga
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información de los congresos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <Calendar className="w-12 h-12 text-[#e87729]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">
                Congresos de Investigación CENID
              </h1>
              <p className="text-xl text-green-100 mt-2">
                {fechaCongresos}
              </p>
            </div>
          </div>
          <p className="text-lg text-green-100 max-w-4xl">
            Compartir ideas, estudios e investigaciones para fomentar el intercambio de ideas y diálogo académico
          </p>
        </div>
      </section>

      {/* Información Principal 
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <Users className="w-8 h-8 text-[#0b742f]" />
                Programa de Congresos de Investigación del Conocimiento
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Modalidad virtual y en diferido
              </p>

              <div className="space-y-6 text-gray-700">
                <p className="text-lg leading-relaxed">
                En el CENID estamos viendo hacia el futuro y sabedores de que se debe evolucionar para satisfacer las necesidades de científicos, profesionales e investigadores nacionales e internacionales, con eso en mente evolucionamos para llegar a nuevos horizontes.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <p className="font-semibold text-blue-800 mb-2">Importante:</p>
                <p className="text-blue-700">
                  A partir de esta fecha se presentan cambios importantes en lo que anteriormente se conocían como congresos ahora se hará referencia a <strong>congresos de investigación</strong>.
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
                <p className="font-semibold text-yellow-800 mb-2">Cambios en la estructura:</p>
                <ul className="list-disc list-inside text-yellow-700 space-y-1">
                  <li>La presentación PowerPoint se sustituye por <strong>presentación en video</strong></li>
                  <li>Se dispondrán de <strong>dos planes para la participación</strong></li>
                </ul>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Planes de Participación 
      {planes.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
              <div className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Award className="w-8 h-8 text-[#e87729]" />
                  Opciones de Participación
                </h2>

                <div className={`grid grid-cols-1 ${planes.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl mx-auto'} gap-6`}>
                  {planes.map((plan, idx) => (
                    <div
                      key={idx}
                      className={`group bg-gradient-to-br ${plan.bgGradient} rounded-2xl p-8 border-2 ${plan.borderColor} ${plan.hoverBorder} transition-all duration-300 hover:shadow-2xl relative overflow-hidden`}
                    >
                      {plan.destacado && (
                        <div className="absolute top-4 right-4 bg-[#e87729] text-white px-3 py-1 rounded-full text-xs font-bold">
                          RECOMENDADO
                        </div>
                      )}
                      
                      <div className="text-center mb-6">
                        <div className={`inline-block ${plan.badgeBg} text-white px-4 py-2 rounded-full text-sm font-bold mb-3`}>
                          {plan.nombre.toUpperCase()}
                        </div>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-5xl font-bold text-gray-900">${plan.precio}</span>
                          <span className="text-gray-600">.00</span>
                        </div>
                        <p className="text-gray-600 mt-2">{plan.moneda} {plan.tipo}</p>
                      </div>
                      
                      <div className="space-y-4">
                        <p className="text-gray-700 font-semibold">{plan.descripcion}</p>
                        <div className="space-y-3">
                          {plan.beneficios.map((beneficio, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <CheckCircle className={`w-5 h-5 ${plan.checkColor} flex-shrink-0 mt-0.5`} />
                              <span className="text-sm text-gray-700">{beneficio}</span>
                            </div>
                          ))}
                        </div>
                        
                        {plan.nota && (
                          <div className="bg-red-100 border-l-4 border-red-500 p-3 rounded-r mt-4">
                            <p className="text-xs text-red-800 font-semibold">NOTA: {plan.nota}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      */}

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <SNIICongressInfoSection className="" />
        </div>
      </section>

      {/* Lista de Congresos */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <Presentation className="w-8 h-8 text-[#0b742f]" />
                Congresos de Investigación
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Todos los congresos se realizarán el {fechaCongresos}
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {congresos.map((congreso, index) => {
                  const IconComponent = congreso.icono;
                  return (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 shadow-lg hover:shadow-xl hover:border-[#0b742f] transition-all duration-300 p-6 flex flex-col h-full"
                    >
                      <div className={`bg-gradient-to-r ${congreso.color} p-4 rounded-lg mb-6`}>
                        <div className="flex items-center gap-3 text-white">
                          <IconComponent className="w-8 h-8" />
                          <h3 className="text-xl font-bold">{congreso.titulo}</h3>
                        </div>
                      </div>

                      <h4 className="font-bold text-gray-900 mb-3 text-lg leading-tight">
                        {congreso.nombre}
                      </h4>

                      <div className="flex items-center gap-2 text-gray-600 mb-4">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{congreso.fecha}</span>
                      </div>

                      <div className="mt-auto pt-2">
                        <Link
                          to={congreso.link}
                          className="inline-flex items-center gap-2 bg-[#0b742f] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#085a24] transition-colors duration-300 w-full justify-center"
                        >
                          Más información
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Acceso a la Plataforma */}
      <section className="py-16 bg-gradient-to-br from-[#e87729] to-[#d66820] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Acceso a la Plataforma</h2>
            <p className="text-xl text-orange-100 mb-8">
              El usuario es el correo con el cual se inscribió a la plataforma
            </p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <Link
                to="/congresos/acceso"
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                <Globe className="w-8 h-8 mx-auto mb-3" />
                <p className="font-semibold">Inscripción</p>
              </Link>
              
              <Link
                to="/congresos/acceso"
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                <Mail className="w-8 h-8 mx-auto mb-3" />
                <p className="font-semibold">Recuperar password</p>
              </Link>
              
              <a
                href="https://congresos.cenid.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                <Video className="w-8 h-8 mx-auto mb-3" />
                <p className="font-semibold">Entrar al Congreso</p>
              </a>
            </div>

            <div className="mt-8">
              <a
                href="https://congresos.cenid.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[#e87729] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-300"
              >
                <ExternalLink className="w-5 h-5" />
                Ir a la Plataforma de Congresos
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Llamada a la acción */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">¿Listo para participar?</h2>
            <p className="text-xl text-green-100 mb-8">
              Únete a nuestros congresos de investigación y comparte tu conocimiento con la comunidad académica internacional
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://congresos.cenid.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#e87729] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#d66820] transition-colors duration-300"
              >
                <Calendar className="w-5 h-5" />
                Inscribirse Ahora
              </a>
              <a
                href="mailto:congresos@cenid.org"
                className="inline-flex items-center gap-2 bg-white text-[#0b742f] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-300"
              >
                <Mail className="w-5 h-5" />
                Solicitar Información
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Congresos;